import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { ClipboardCopy, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

import { Sheet, SheetContent, SheetTitle } from 'components/ui/sheet';
import { Button } from 'components/ui/button';
import { Skeleton } from 'components/ui/skeleton';
import { Typography } from 'components/ui/typography';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'components/ui/accordion';
import { GET_PRODUCT_BY_ID } from 'app/graphql';
import { Marketplace } from 'app/graphql/generated/cart/graphql';
import type {
  CreateOrder_ProductByIdQuery,
  CreateOrder_ProductByIdQueryVariables,
} from 'app/graphql/generated/cart/graphql';
import { Currency } from 'app/graphql/generated/admin/graphql';
import { createFallbackImageUrl } from 'pages/Catalog/utils';
import Logo from 'assets/icons/logo-icon-light.png';

export type CatalogProduct = {
  id: string;
  title: string;
  url: string;
  images?: Array<{ url: string }> | null;
  price: number;
  currency: Currency;
  marketplace: Marketplace;
  vendor?: string | null;
  storeName?: string;
  storeLogo?: string;
  storeCommissions?: number;
};

type Props = {
  product: CatalogProduct | null;
  onOpenChange: (open: boolean) => void;
};

export const ProductDetail = ({ product, onOpenChange }: Props) => {
  return (
    <Sheet open={!!product} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto p-0 sm:max-w-xl">
        {product ? <ProductDetailContent product={product} /> : null}
      </SheetContent>
    </Sheet>
  );
};

type FetchedProduct = NonNullable<CreateOrder_ProductByIdQuery['productByID']>;
type FetchedVariant = FetchedProduct['variants'][number];

const ProductDetailContent = ({ product }: { product: CatalogProduct }) => {
  // `no-cache` is load-bearing: the cart-api and admin-api both return
  // ShopifyProduct/AmazonProduct entities keyed by the same id, but with
  // incompatible `price` shapes (admin = Float, cart = { value, currency,
  // displayValue }). Writing the cart response into the normalized cache
  // would overwrite the catalog list's numeric price with an object and
  // crash the card on the next render.
  const { data, loading } = useQuery<
    CreateOrder_ProductByIdQuery,
    CreateOrder_ProductByIdQueryVariables
  >(GET_PRODUCT_BY_ID, {
    variables: { input: { id: product.id, marketplace: product.marketplace } },
    fetchPolicy: 'no-cache',
  });

  const fetched = data?.productByID ?? null;

  const fetchedImages = (fetched?.images ?? []).filter((i) => !!i?.url);
  const fallbackImages = (product.images ?? []).filter((i) => !!i?.url);
  const images = fetchedImages.length > 0 ? fetchedImages : fallbackImages;

  const [activeImage, setActiveImage] = useState(0);
  // Reset the active image when more images arrive from the detail query so
  // the index doesn't dangle past the (smaller) fallback list.
  useEffect(() => {
    setActiveImage(0);
  }, [fetched?.id]);

  const heroImage = images[activeImage]?.url;

  const title = fetched?.title ?? product.title;
  const description = fetched?.description?.trim() ?? '';
  const vendor = fetched?.vendor ?? product.vendor ?? null;

  const variants = useMemo(
    () =>
      (fetched?.variants ?? []).filter(
        (v): v is FetchedVariant & { __typename: 'ShopifyVariant' } =>
          v.__typename === 'ShopifyVariant',
      ),
    [fetched?.variants],
  );
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
  useEffect(() => {
    setSelectedVariantId(variants[0]?.id ?? null);
  }, [fetched?.id, variants]);

  const selectedVariant = variants.find((v) => v.id === selectedVariantId);

  const priceCents = selectedVariant?.priceCents ?? fetched?.price?.value ?? product.price;
  const currency = (fetched?.price?.currency ?? product.currency) as Currency;
  const formattedPrice = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(priceCents / 100);

  const marketplaceLabel = product.marketplace === Marketplace.Shopify ? 'Shopify' : 'Amazon';
  const fallbackBadge = (product.storeName ?? 'r').charAt(0).toLowerCase();

  const isAvailable = fetched
    ? selectedVariant
      ? selectedVariant.isAvailable
      : fetched.isAvailable
    : null;

  return (
    <div className="flex flex-col">
      <div className="relative aspect-square w-full overflow-hidden border-b border-[var(--line)] bg-[#f3eedb]">
        <Typography
          variant="h4"
          as="div"
          className="absolute right-3 top-3 z-10 grid h-8 w-8 place-items-center border border-[var(--line)] bg-[var(--rye-paper)] font-display text-[11px] lowercase text-[var(--rye-ink)]"
        >
          {product.storeLogo ? (
            <img
              src={product.storeLogo}
              alt={product.storeName ? `${product.storeName} logo` : ''}
              className="h-full w-full object-contain"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : (
            <img
              src={createFallbackImageUrl(product.url, product.marketplace)}
              alt=""
              className="h-full w-full object-contain"
              onError={(e) => {
                e.currentTarget.replaceWith(document.createTextNode(fallbackBadge));
              }}
            />
          )}
        </Typography>
        {heroImage ? (
          <img src={heroImage} alt={title} className="h-full w-full object-contain" />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <img src={Logo} alt="" className="h-16 w-16 opacity-30" />
          </div>
        )}
      </div>

      {images.length > 1 && (
        <div className="flex gap-2 overflow-x-auto border-b border-[var(--line)] bg-[var(--surface)] p-3">
          {images.map((img, i) => (
            <button
              key={img.url + i}
              type="button"
              onClick={() => setActiveImage(i)}
              className={
                'h-16 w-16 flex-shrink-0 overflow-hidden border bg-[#f3eedb] transition-colors ' +
                (i === activeImage
                  ? 'border-[var(--accent)]'
                  : 'border-[var(--line)] hover:border-[var(--line-strong)]')
              }
              aria-label={`Show image ${i + 1}`}
              aria-pressed={i === activeImage}
            >
              <img src={img.url} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-5 px-6 py-6">
        <Typography variant="eyebrow" as="div" className="flex flex-wrap items-center gap-2">
          <span>{marketplaceLabel}</span>
          {product.storeName && (
            <>
              <span aria-hidden="true">·</span>
              <span className="text-[var(--text)]">{product.storeName}</span>
            </>
          )}
          {isAvailable === false && (
            <>
              <span aria-hidden="true">·</span>
              <span className="text-[var(--rye-red)]">Out of stock</span>
            </>
          )}
        </Typography>

        <SheetTitle asChild>
          <Typography variant="h2" as="h2">
            {title}
          </Typography>
        </SheetTitle>

        {vendor && (
          <Typography variant="mono" as="div" className="text-[12px] text-[var(--text-muted)]">
            Vendor · <span className="text-[var(--text)]">{vendor}</span>
          </Typography>
        )}

        <div className="flex items-baseline gap-3">
          <Typography variant="h2" as="span" className="text-[var(--text)]">
            {formattedPrice}
          </Typography>
          <Typography variant="eyebrow" as="span">
            {currency}
          </Typography>
        </div>

        {variants.length > 1 && (
          <div className="flex flex-col gap-2">
            <Typography variant="eyebrow" as="div">
              Variants ({variants.length})
            </Typography>
            <div className="flex flex-wrap gap-2">
              {variants.map((v) => {
                const isSelected = v.id === selectedVariantId;
                const label = v.title ?? v.name ?? v.id;
                return (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => setSelectedVariantId(v.id)}
                    disabled={!v.isAvailable}
                    className={
                      'border px-3 py-1.5 text-[13px] transition-colors disabled:cursor-not-allowed disabled:opacity-50 ' +
                      (isSelected
                        ? 'border-[var(--accent)] bg-[var(--accent-soft)] text-[var(--text)]'
                        : 'border-[var(--line)] bg-[var(--surface)] text-[var(--text-muted)] hover:border-[var(--line-strong)] hover:text-[var(--text)]')
                    }
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {typeof product.storeCommissions === 'number' && product.storeCommissions > 0 && (
          <Typography
            variant="mono"
            as="div"
            className="flex items-center justify-between border border-[var(--line)] bg-[var(--surface-2)] px-4 py-3 text-[12px] text-[var(--text-muted)]"
          >
            <span>Commission</span>
            <span className="text-[var(--text)]">{product.storeCommissions}%</span>
          </Typography>
        )}

        <div className="flex flex-col gap-2 pt-2">
          <Button asChild className="gap-2">
            <a href={product.url} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4" />
              View on {marketplaceLabel}
            </a>
          </Button>
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => {
              navigator.clipboard.writeText(product.id);
              toast.success('Copied product ID to clipboard');
            }}
          >
            <ClipboardCopy className="h-4 w-4" />
            Copy product ID
          </Button>
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => {
              navigator.clipboard.writeText(product.url);
              toast.success('Copied product URL to clipboard');
            }}
          >
            <ClipboardCopy className="h-4 w-4" />
            Copy product URL
          </Button>
        </div>

        <Description text={description} loading={loading && !fetched} />

        <div className="border-t border-[var(--line)] pt-4">
          <div className="flex items-center justify-between gap-4">
            <Typography variant="eyebrow" as="span">
              Product ID
            </Typography>
            <Typography variant="mono" as="span" className="truncate text-[var(--text)]">
              {product.id}
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

// Shopify product descriptions sometimes ship as HTML. We render plain
// text only — converting tags to whitespace — to avoid running arbitrary
// merchant markup in the console.
const stripHtml = (html: string) => {
  const withBreaks = html
    .replace(/<\/(p|div|li|h[1-6])>/gi, '\n')
    .replace(/<br\s*\/?>(\s*)/gi, '\n');
  return withBreaks
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const Description = ({ text, loading }: { text: string; loading: boolean }) => {
  if (!loading && !text) {
    return null;
  }

  return (
    <Accordion type="single" collapsible className="border-t border-[var(--line)]">
      <AccordionItem value="description" className="border-b-0">
        <AccordionTrigger className="py-3 hover:no-underline">
          <Typography variant="eyebrow" as="span">
            Description
          </Typography>
        </AccordionTrigger>
        <AccordionContent className="pb-3 pt-0">
          {loading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-11/12" />
              <Skeleton className="h-3 w-9/12" />
            </div>
          ) : (
            <Typography
              variant="small"
              as="div"
              className="whitespace-pre-line leading-relaxed text-[var(--text)]"
            >
              {stripHtml(text)}
            </Typography>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
